import { Component, OnInit, OnDestroy, HostListener, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { isLayoutFillRoute } from './layout-fill';
import { AuthService } from '@core/services/auth.service';
import { ConfirmDialogService } from '@shared/components/confirm-dialog/confirm-dialog.service';
import { OrganizationService, UserOrganization, CreateOrganizationRequest } from '@core/services/organization.service';
import { NotificationService } from '@core/services/notification.service';
import { FeatureFlagService, UX_FEATURE_FLAGS } from '@core/services/feature-flag.service';
import { OnboardingProgressService } from '@core/services/onboarding-progress.service';
import { UxPersonaService } from '@core/services/ux-persona.service';
import { AppState } from '@core/store';
import { selectUserProfile, selectUserFullName } from '@features/settings/store/settings.selectors';
import { loadUserProfile } from '@features/settings/store/settings.actions';
import { UserProfile } from '@features/settings/models/settings.models';
import { PageTitleService } from '@core/services/page-title.service';
import { PageHeaderIconComponent } from '../page-header-icon/page-header-icon.component';
import { CountryPhoneInputComponent } from '@shared/components/country-phone-input/country-phone-input.component';
import { ModalComponent } from '@shared/components/modal/modal.component';
import { PeSelectComponent } from '@shared/components/pe-select/pe-select.component';

interface NavItem {
  label: string;
  route: string;
  icon: string;
  badge?: string;
  exact?: boolean;
  queryParams?: Record<string, string>;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, ReactiveFormsModule, CountryPhoneInputComponent, ModalComponent, PageHeaderIconComponent],
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss']
})
export class LayoutComponent implements OnInit, OnDestroy {
  private store = inject(Store<AppState>);
  private authService = inject(AuthService);
  private confirmDialogService = inject(ConfirmDialogService);
  private router = inject(Router);
  private orgService = inject(OrganizationService);
  private notificationService = inject(NotificationService);
  private featureFlags = inject(FeatureFlagService);
  private onboardingProgress = inject(OnboardingProgressService);
  private personaService = inject(UxPersonaService);
  private fb = inject(FormBuilder);
  private pageTitleService = inject(PageTitleService);
  private destroy$ = new Subject<void>();

  pageTitle$ = this.pageTitleService.title$;
  pageHeader$ = this.pageTitleService.header$;

  userProfile$: Observable<UserProfile | null>;
  userFullName$: Observable<string>;
  organizations$: Observable<UserOrganization[]>;
  currentOrg$: Observable<UserOrganization | null>;
  orgLoading$: Observable<boolean>;

  /** When true, content pane fills the viewport (no outer scroll). See layout-fill.ts. */
  isContentFillMode = false;
  sidebarVisible = window.innerWidth > 768;
  userDropdownVisible = false;
  orgDropdownVisible = false;
  showCreateOrgModal = false;
  createOrgForm: FormGroup;
  isCreatingOrg = false;
  isSwitchingOrg = false;

  /** When ux_collapsed_nav is on, advanced items stay hidden until first parse or Show more. */
  collapsedNavEnabled = false;
  showAdvancedNav = false;
  private advancedManuallyExpanded = false;

  private readonly fullNavSections: NavSection[] = [
    {
      title: 'OVERVIEW',
      items: [
        { label: 'Dashboard', route: '/dashboard', icon: 'dashboard' },
        { label: 'Documents', route: '/documents', icon: 'documents' },
        { label: 'Activity', route: '/activity', icon: 'activity' },
        { label: 'Playground', route: '/playground', icon: 'playground' }
      ]
    },
    {
      title: 'TEMPLATES',
      items: [
        { label: 'All Templates', route: '/templates', icon: 'template', exact: true },
        { label: 'Create Template', route: '/templates/create', icon: 'add' }
      ]
    },
    {
      title: 'API & WEBHOOKS',
      items: [
        { label: 'API Keys', route: '/api-keys', icon: 'key' },
        { label: 'Webhooks', route: '/webhooks', icon: 'webhook' }
      ]
    },
    {
      title: 'REPORTS',
      items: [
        { label: 'Analytics', route: '/analytics', icon: 'analytics' },
        { label: 'Usage Reports', route: '/usage-reports', icon: 'report' }
      ]
    },
    {
      title: 'SETTINGS',
      items: [
        { label: 'Billing & Credits', route: '/billing', icon: 'billing' },
        { label: 'Settings', route: '/settings', icon: 'settings' }
      ]
    },
    {
      title: 'SUPPORT',
      items: [
        { label: 'Help', route: '/help', icon: 'help' },
        { label: 'Contact support', route: '/support', icon: 'support' }
      ]
    }
  ];

  private readonly essentialItems: NavItem[] = [
    { label: 'Dashboard', route: '/dashboard', icon: 'dashboard' },
    { label: 'Playground', route: '/playground', icon: 'playground' },
    { label: 'Documents', route: '/documents', icon: 'documents' },
    { label: 'Activity', route: '/activity', icon: 'activity' },
    { label: 'Templates', route: '/templates', icon: 'template', exact: true },
    { label: 'API Keys', route: '/api-keys', icon: 'key' },
    { label: 'Billing', route: '/billing', icon: 'billing' },
    { label: 'Settings', route: '/settings', icon: 'settings' }
  ];

  private readonly advancedItems: NavItem[] = [
    { label: 'Webhooks', route: '/webhooks', icon: 'webhook' },
    { label: 'Analytics', route: '/analytics', icon: 'analytics' },
    { label: 'Usage Reports', route: '/usage-reports', icon: 'report' },
    { label: 'Help center', route: '/help', icon: 'help' },
    { label: 'Contact support', route: '/support', icon: 'support' }
  ];

  navSections: NavSection[] = this.fullNavSections;

  constructor() {
    this.userProfile$ = this.store.select(selectUserProfile);
    this.userFullName$ = this.store.select(selectUserFullName);
    this.organizations$ = this.orgService.organizations$;
    this.currentOrg$ = this.orgService.currentOrganization$;
    this.orgLoading$ = this.orgService.loading$;

    this.createOrgForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      company_size: ['', Validators.required],
      industry: ['', Validators.required],
      country: ['', Validators.required],
      set_as_current: [true]
    });
  }

  ngOnInit(): void {
    this.store.dispatch(loadUserProfile());
    this.loadOrganizations();
    this.updateContentFillMode(this.router.url);
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe(event => this.updateContentFillMode(event.urlAfterRedirects));

    this.featureFlags.ensureLoaded()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.applyNavMode());

    this.featureFlags.flags$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.applyNavMode());

    this.onboardingProgress.onRefresh$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.applyNavMode());

    this.personaService.persona$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.applyNavMode());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateContentFillMode(url: string): void {
    this.isContentFillMode = isLayoutFillRoute(url);
  }

  private applyNavMode(): void {
    this.collapsedNavEnabled = this.featureFlags.isEnabled(UX_FEATURE_FLAGS.collapsedNav);
    const prebuiltDefault = this.featureFlags.isEnabled(UX_FEATURE_FLAGS.templatePrebuiltDefault);
    const examplesGallery = this.featureFlags.isEnabled(UX_FEATURE_FLAGS.examplesGallery);
    const personaOn = this.featureFlags.isEnabled(UX_FEATURE_FLAGS.personaFirstRun);
    const persona = this.personaService.persona;

    const createTemplateItem: NavItem = prebuiltDefault
      ? { label: 'Create Template', route: '/templates', icon: 'add', queryParams: { tab: 'prebuilt' } }
      : { label: 'Create Template', route: '/templates/create', icon: 'add' };

    const fullSections: NavSection[] = this.fullNavSections.map(section => {
      if (section.title === 'TEMPLATES') {
        return {
          ...section,
          items: section.items.map(item =>
            item.label === 'Create Template' ? createTemplateItem : item
          )
        };
      }
      if (section.title === 'OVERVIEW' && examplesGallery) {
        return {
          ...section,
          items: [
            ...section.items,
            { label: 'Examples', route: '/examples', icon: 'playground' }
          ]
        };
      }
      if (section.title === 'OVERVIEW' && personaOn && persona) {
        return {
          ...section,
          items: this.orderMainItems(section.items, persona)
        };
      }
      return section;
    });

    // Re-apply OVERVIEW ordering / persona emphasis after examples insert
    if (personaOn && persona) {
      const mainIdx = fullSections.findIndex(s => s.title === 'OVERVIEW');
      if (mainIdx >= 0) {
        let mainItems = [...fullSections[mainIdx].items];
        if (persona === 'developer') {
          const apiSectionIdx = fullSections.findIndex(s => s.title === 'API & WEBHOOKS');
          if (apiSectionIdx >= 0) {
            const apiKeys = fullSections[apiSectionIdx].items.find(i => i.route === '/api-keys');
            if (apiKeys && !mainItems.some(i => i.route === '/api-keys')) {
              mainItems = [...mainItems, apiKeys];
              fullSections[apiSectionIdx] = {
                ...fullSections[apiSectionIdx],
                items: fullSections[apiSectionIdx].items.filter(i => i.route !== '/api-keys')
              };
            }
          }
        }
        fullSections[mainIdx] = {
          ...fullSections[mainIdx],
          items: this.orderMainItems(mainItems, persona)
        };
      }
    }

    if (!this.collapsedNavEnabled) {
      this.navSections = fullSections;
      this.showAdvancedNav = true;
      return;
    }

    this.showAdvancedNav =
      this.authService.hasCompletedFirstParse() || this.advancedManuallyExpanded;

    let essentials = [...this.essentialItems];
    if (examplesGallery) {
      essentials = [
        ...essentials.slice(0, 2),
        { label: 'Examples', route: '/examples', icon: 'playground' },
        ...essentials.slice(2)
      ];
    }
    if (personaOn && persona) {
      essentials = this.orderEssentialItems(essentials, persona);
    }

    const sections: NavSection[] = [
      { title: 'OVERVIEW', items: essentials }
    ];

    if (this.showAdvancedNav) {
      sections.push({ title: 'ADVANCED', items: this.advancedItems });
    }

    this.navSections = sections;
  }

  /** Developer → API Keys early; Operations → Documents / Templates early. */
  private orderMainItems(items: NavItem[], persona: 'developer' | 'operations'): NavItem[] {
    const priority =
      persona === 'developer'
        ? ['/dashboard', '/playground', '/examples', '/api-keys', '/documents', '/activity', '/templates']
        : ['/dashboard', '/documents', '/playground', '/examples', '/activity', '/templates', '/api-keys'];
    return this.sortByRoutePriority(items, priority);
  }

  private orderEssentialItems(items: NavItem[], persona: 'developer' | 'operations'): NavItem[] {
    const priority =
      persona === 'developer'
        ? ['/dashboard', '/playground', '/examples', '/api-keys', '/documents', '/activity', '/templates', '/billing', '/settings']
        : ['/dashboard', '/documents', '/playground', '/examples', '/activity', '/templates', '/api-keys', '/billing', '/settings'];
    return this.sortByRoutePriority(items, priority);
  }

  private sortByRoutePriority(items: NavItem[], priority: string[]): NavItem[] {
    return [...items].sort((a, b) => {
      const ai = priority.indexOf(a.route);
      const bi = priority.indexOf(b.route);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
  }

  expandAdvancedNav(): void {
    this.advancedManuallyExpanded = true;
    this.applyNavMode();
  }

  loadOrganizations(): void {
    this.orgService.loadOrganizations().subscribe({
      error: (err) => console.error('Failed to load organizations:', err)
    });
  }

  toggleSidebar(): void {
    this.sidebarVisible = !this.sidebarVisible;
  }

  @HostListener('document:pe-close-app-menus')
  closeAppHeaderMenus(): void {
    this.userDropdownVisible = false;
    this.orgDropdownVisible = false;
  }

  toggleUserDropdown(): void {
    PeSelectComponent.closeOpenMenus();
    this.userDropdownVisible = !this.userDropdownVisible;
    if (this.userDropdownVisible) {
      this.orgDropdownVisible = false;
    }
  }

  closeUserDropdown(): void {
    this.userDropdownVisible = false;
  }

  getUserInitials(profile: UserProfile | null): string {
    if (!profile) return 'U';
    const firstInitial = profile.first_name?.charAt(0) || '';
    const lastInitial = profile.last_name?.charAt(0) || '';
    return (firstInitial + lastInitial).toUpperCase() || 'U';
  }

  getUserRole(profile: UserProfile | null): string {
    if (!profile) return 'User';
    return profile.role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  getUserDisplayName(profile: UserProfile | null): string {
    if (!profile) return 'Account';
    const fullName = [profile.first_name, profile.last_name].filter(Boolean).join(' ').trim();
    if (fullName) return fullName;
    if (profile.email) return profile.email.split('@')[0];
    return 'Account';
  }

  getHeaderRoleLabel(profile: UserProfile | null): string {
    const orgRole = this.orgService.getCurrentOrganization()?.role;
    if (orgRole) {
      return orgRole.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
    return this.getUserRole(profile);
  }

  logout(): void {
    this.closeUserDropdown();
    this.confirmDialogService.confirm({
      title: 'Sign out',
      message: 'You will need to sign in again to access your workspace. Session data stored on this device will be cleared.',
      confirmLabel: 'Sign out',
      cancelLabel: 'Stay signed in',
      icon: 'sign-out',
      variant: 'default'
    }).pipe(filter(Boolean), take(1)).subscribe(() => {
      this.authService.logout().subscribe({
        next: () => {
          console.log('[LAYOUT] Logout successful, refresh token cookie cleared');
        },
        error: (err) => {
          console.error('[LAYOUT] Logout error:', err);
        }
      });
    });
  }

  navigateToProfile(): void {
    this.closeUserDropdown();
    this.router.navigate(['/settings']);
  }

  navigateToSettings(): void {
    this.closeUserDropdown();
    this.router.navigate(['/settings/organization']);
  }

  navigateToBilling(): void {
    this.closeUserDropdown();
    this.router.navigate(['/billing']);
  }

  isActiveRoute(route: string): boolean {
    return this.router.url.startsWith(route);
  }

  toggleOrgDropdown(): void {
    PeSelectComponent.closeOpenMenus();
    this.orgDropdownVisible = !this.orgDropdownVisible;
    if (this.orgDropdownVisible) {
      this.userDropdownVisible = false;
    }
  }

  closeOrgDropdown(): void {
    this.orgDropdownVisible = false;
  }

  switchOrganization(org: UserOrganization): void {
    if (org.is_current || this.isSwitchingOrg) return;

    this.isSwitchingOrg = true;
    this.orgService.switchOrganization(org.organization_id).subscribe({
      next: (response) => {
        this.notificationService.showSuccess(response.message);
        this.closeOrgDropdown();
        this.isSwitchingOrg = false;
        window.location.reload();
      },
      error: (err) => {
        this.notificationService.showError(err.error?.detail || 'Failed to switch organization');
        this.isSwitchingOrg = false;
      }
    });
  }

  openCreateOrgModal(): void {
    this.showCreateOrgModal = true;
    this.closeOrgDropdown();
    this.createOrgForm.reset({ set_as_current: true });
  }

  closeCreateOrgModal(): void {
    this.showCreateOrgModal = false;
  }

  createOrganization(): void {
    if (this.createOrgForm.invalid || this.isCreatingOrg) return;

    this.isCreatingOrg = true;
    const request: CreateOrganizationRequest = this.createOrgForm.value;

    this.orgService.createOrganization(request).subscribe({
      next: (response) => {
        this.notificationService.showSuccess(response.message);
        this.closeCreateOrgModal();
        this.isCreatingOrg = false;
        if (response.is_current) {
          window.location.reload();
        }
      },
      error: (err) => {
        this.notificationService.showError(err.error?.detail || 'Failed to create organization');
        this.isCreatingOrg = false;
      }
    });
  }

  getRoleBadgeClass(role: string): string {
    switch (role.toLowerCase()) {
      case 'owner': return 'bg-purple-100 text-purple-700';
      case 'admin': return 'bg-blue-100 text-blue-700';
      case 'member': return 'bg-green-100 text-green-700';
      case 'viewer': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  }

  getTourId(item: NavItem): string | null {
    const tourMap: Record<string, string> = {
      '/playground': 'nav-playground',
      '/templates': 'nav-templates',
      '/api-keys': 'nav-api-keys',
      '/billing': 'nav-billing',
      '/help': 'nav-help'
    };
    return tourMap[item.route] ?? null;
  }
}
